import { useNavigate } from '@tanstack/react-router'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext, useMemo, useRef } from 'react'
import type { ClientConnectionConfig } from '@companion-app/shared/Model/Connections.js'
import { ModuleInstanceType } from '@companion-app/shared/Model/Instance.js'
import { GenericConfirmModal, type GenericConfirmModalRef } from '~/Components/GenericConfirmModal.js'
import { Grid } from '~/Components/Grid'
import { InstanceGenericEditPanel } from '~/Instances/InstanceEdit/InstanceEditPanel.js'
import type { InstanceEditPanelService } from '~/Instances/InstanceEdit/InstanceEditPanelService.js'
import type { InstanceEditPanelStore } from '~/Instances/InstanceEdit/InstanceEditPanelStore.js'
import { trpc, useMutationExt, type RouterInput } from '~/Resources/TRPC.js'
import { RootAppStoreContext } from '~/Stores/RootAppStore.js'
import { ConnectionEditPanelHeading } from './ConnectionEditPanelHeading.js'

interface ConnectionEditPanelProps {
	connectionId: string
}

export const ConnectionEditPanel = observer(function ConnectionEditPanel({ connectionId }: ConnectionEditPanelProps) {
	const { connections } = useContext(RootAppStoreContext)

	const confirmModalRef = useRef<GenericConfirmModalRef>(null)
	const service = useInstanceEditPanelService(confirmModalRef, connectionId)

	const connectionInfo: ClientConnectionConfig | undefined = connections.getInfo(connectionId)

	if (!connectionInfo) {
		return (
			<Grid.Row className="edit-connection">
				<Grid.Col xs={12}>
					<p>Connection not found</p>
				</Grid.Col>
			</Grid.Row>
		)
	}

	return (
		<>
			<GenericConfirmModal ref={confirmModalRef} />

			<ConnectionEditPanelHeading connectionInfo={connectionInfo} closeConfigurePanel={service.closePanel} />

			<InstanceGenericEditPanel<ClientConnectionConfig>
				instanceInfo={connectionInfo}
				service={service}
				changeModuleDangerMessage={
					<>
						Changing the module type can break the connection and corrupt any existing actions and feedbacks. Only use
						this if you are sure of what you are doing.
					</>
				}
			/>
		</>
	)
})

function useInstanceEditPanelService(
	confirmModalRef: React.RefObject<GenericConfirmModalRef | null>,
	instanceId: string
): InstanceEditPanelService<ClientConnectionConfig> {
	const navigate = useNavigate({ from: `/connections/$connectionId` })
	const closePanel = useCallback(() => {
		void navigate({ to: `/connections` })
	}, [navigate])

	const setConfigMutation = useMutationExt(trpc.instances.connections.setConfig.mutationOptions())
	const setModuleAndVersionMutation = useMutationExt(trpc.instances.connections.setModuleAndVersion.mutationOptions())
	const deleteMutation = useMutationExt(trpc.instances.connections.delete.mutationOptions())

	const setModuleAndVersion = useCallback(
		async (moduleId: string, versionId: string | null): Promise<string | null> =>
			setModuleAndVersionMutation.mutateAsync({ connectionId: instanceId, moduleId, versionId }),
		[setModuleAndVersionMutation, instanceId]
	)

	const deleteInstance = useCallback(
		(currentLabel: string) => {
			confirmModalRef.current?.show(
				'Delete connection',
				[
					`Are you sure you want to delete "${currentLabel}"?`,
					'This will remove all actions and feedbacks associated with this connection.',
				],
				'Delete',
				() => {
					deleteMutation.mutateAsync({ connectionId: instanceId }).catch((e) => {
						console.error('Delete failed', e)
					})
					closePanel()
				}
			)
		},
		[deleteMutation, confirmModalRef, instanceId, closePanel]
	)

	const saveConfig = useCallback(
		async (panelStore: InstanceEditPanelStore<ClientConnectionConfig>): Promise<string | null> => {
			const saveLabel = panelStore.labelValue

			const saveConfigProps: RouterInput['instances']['connections']['setConfig'] = {
				connectionId: instanceId,
				label: saveLabel,
				enabled: panelStore.enabled,
				updatePolicy: panelStore.updatePolicy,
			}

			if (panelStore.isLoading) throw new Error('Connection is still loading, cannot save changes')

			// Only present when a running child reported its config fields
			const configAndSecrets = panelStore.configAndSecrets
			if (configAndSecrets) {
				saveConfigProps.config = configAndSecrets.config
				saveConfigProps.secrets = configAndSecrets.secrets
			}

			const err: string | null = await setConfigMutation.mutateAsync(saveConfigProps)

			if (err === 'invalid label') {
				return `The label "${saveLabel}" is not valid`
			} else if (err === 'duplicate label') {
				return `The label "${saveLabel}" is already in use. Please use a unique label for this connection`
			} else if (err) {
				return `Unable to save connection config: "${err}"`
			} else {
				// The subscription will deliver the freshly saved config; just clear the dirty tracking
				panelStore.markSaved()

				return null
			}
		},
		[setConfigMutation, instanceId]
	)

	return useMemo(
		() => ({
			moduleType: ModuleInstanceType.Connection,
			instanceId,

			moduleTypeDisplayName: 'connection',

			watchConfig: (handlers) =>
				trpc.instances.connections.watchEdit.subscriptionOptions({ connectionId: instanceId }, handlers),

			deleteInstance,

			saveConfig,

			setModuleAndVersion,

			closePanel,
		}),
		[instanceId, deleteInstance, saveConfig, setModuleAndVersion, closePanel]
	)
}
